/**
 * Brick Codegen - Main Entry Point
 * Clean architecture with original functionality preserved
 */

import { execa } from "execa";
import fs from "fs-extra";
import { glob } from "glob";
import path from "path";
import pc from "picocolors";
import { BrickConfigLoader } from "./config-loader";
import { BrickCodegenError, createCodegenError, wrapError } from "./errors";
import { Generator } from "./generator";
import { LibraryGenerator } from "./library-generator";
import { Parser } from "./parser";
import { PlatformRegistry } from "./platforms";
import { Scanner } from "./scanner";
import { removePrivateAccessModifiers } from "./signature-mapper";
import { SourceMapRegistry } from "./source-map";

export interface GenerationResult {
  success: boolean;
  moduleCount: number;
  errors: string[];
  warnings: string[];
  generatedFiles: string[];
  duration: number;
  detailedErrors?: BrickCodegenError[]; // Rich error objects for better reporting
}

export interface BrickCodegenConfig {
  projectRoot: string;
  outputIos: string; // Always has value (default: "ios/.brick")
  outputAndroid: string; // Always has value (default: "android/.brick")
  outputAndroidAppPath: string; // Always has value (default: "android/app")
  outputTypescript: string; // Always has value (default: ".brick")
  debug?: boolean;
  platforms?: string[]; // ['ios', 'android'] or subset for platform-specific generation
  libraryMode?: boolean;
  clean?: boolean; // Clean output directories before generating
}

export type BrickCodegenCLIInput = Partial<BrickCodegenConfig> & {
  projectRoot: string;
};

// Internal configuration type used by components
export interface ComponentConfig {
  projectRoot: string;
  outputTypescript: string;
  outputIos: string;
  outputAndroid: string;
  outputAndroidAppPath: string;
  libraryMode?: boolean;
  debug?: boolean;
  selectedPlatforms?: string[];
}

export class BrickCodegen {
  private config: BrickCodegenConfig;
  private scanner: Scanner;
  private parser: Parser;
  private generator: Generator;
  private libraryGenerator: LibraryGenerator;
  private platformRegistry: PlatformRegistry;
  private sourceMap: SourceMapRegistry;

  constructor(config: BrickCodegenCLIInput) {
    this.sourceMap = new SourceMapRegistry();
    // Load brick.json configuration
    const brickConfig = BrickConfigLoader.load(config.projectRoot);
    const mergedBrickConfig = BrickConfigLoader.mergeWithDefaults(brickConfig);
    // Always ensure output paths have values
    this.config = {
      projectRoot: config.projectRoot,
      outputTypescript:
        config.outputTypescript || mergedBrickConfig.output.typescript,
      outputIos: config.outputIos || mergedBrickConfig.output.ios,
      outputAndroid: config.outputAndroid || mergedBrickConfig.output.android,
      outputAndroidAppPath:
        config.outputAndroidAppPath || mergedBrickConfig.output.androidAppPath,
      debug: config.debug || false,
      platforms: config.platforms,
      clean: config.clean !== false, // Default to true unless explicitly set to false
    };

    // Initialize core components (will be properly configured after project detection)
    this.scanner = new Scanner(this.createComponentConfig());
    this.parser = new Parser(this.createComponentConfig());
    this.platformRegistry = new PlatformRegistry(this.createComponentConfig());
    this.generator = new Generator(
      this.createComponentConfig(),
      this.platformRegistry
    );
    this.libraryGenerator = new LibraryGenerator(
      this.createComponentConfig(),
      this.platformRegistry
    );
  }

  /**
   * Creates configuration format for internal components
   */
  private createComponentConfig(): ComponentConfig {
    const selectedPlatforms = this.config.platforms;

    return {
      projectRoot: this.config.projectRoot,
      outputTypescript: this.config.outputTypescript, // Already has default value
      outputIos: this.config.outputIos, // Already has default value
      outputAndroid: this.config.outputAndroid, // Already has default value
      outputAndroidAppPath: this.config.outputAndroidAppPath, // Already has default value
      libraryMode: this.config.libraryMode,
      debug: this.config.debug || false,
      // Add platform filtering for generators
      selectedPlatforms,
    };
  }

  /**
   * Clean output directories before generation
   */
  private async cleanOutputDirectories(isLibrary: boolean): Promise<void> {
    const dirsToClean: string[] = [];

    if (isLibrary) {
      // Library mode: clean typegen directories
      if (!this.config.platforms || this.config.platforms.includes("ios")) {
        const iosTypegen = path.join(this.config.projectRoot, "ios", "typegen");
        dirsToClean.push(iosTypegen);
      }

      if (!this.config.platforms || this.config.platforms.includes("android")) {
        const androidTypegen = path.join(
          this.config.projectRoot,
          "android",
          "src",
          "main",
          "kotlin"
        );

        // For Android, we need to clean the entire kotlin directory to remove all typegen folders
        // since they're nested in package directories
        if (await fs.pathExists(androidTypegen)) {
          // Find all typegen directories recursively
          const typeDirs = await glob("**/typegen", {
            cwd: androidTypegen,
            absolute: true,
          });
          dirsToClean.push(...typeDirs);
        }
      }
    } else {
      // App mode: clean .brick directories
      const typescriptOut = path.join(
        this.config.projectRoot,
        this.config.outputTypescript
      );
      dirsToClean.push(typescriptOut);

      if (!this.config.platforms || this.config.platforms.includes("ios")) {
        const iosOut = path.join(
          this.config.projectRoot,
          this.config.outputIos
        );
        dirsToClean.push(iosOut);
      }

      if (!this.config.platforms || this.config.platforms.includes("android")) {
        const androidOut = path.join(
          this.config.projectRoot,
          this.config.outputAndroid
        );
        dirsToClean.push(androidOut);
      }
    }

    // Safety check: ensure paths are not dangerous
    const projectRoot = path.resolve(this.config.projectRoot);
    for (const dir of dirsToClean) {
      const resolvedDir = path.resolve(dir);

      // Don't delete project root, src, or parent directories
      if (
        resolvedDir === projectRoot ||
        resolvedDir === path.dirname(projectRoot) ||
        resolvedDir.endsWith("/src") ||
        resolvedDir.endsWith("/src/")
      ) {
        this.log(`⚠️  Skipping dangerous path for cleaning: ${resolvedDir}`);
        continue;
      }

      // Clean the directory
      if (await fs.pathExists(resolvedDir)) {
        this.log(`🗑️  Cleaning ${path.relative(projectRoot, resolvedDir)}/`);
        try {
          await fs.remove(resolvedDir);
        } catch (error) {
          this.log(
            `⚠️  Failed to clean ${resolvedDir}: ${
              error instanceof Error ? error.message : String(error)
            }`
          );
        }
      }
    }
  }

  /**
   * Main generation method
   */
  async generate(): Promise<GenerationResult> {
    const startTime = Date.now();
    const result: GenerationResult = {
      success: false,
      moduleCount: 0,
      errors: [],
      warnings: [],
      generatedFiles: [],
      duration: 0,
    };

    try {
      // 1. Detect if this is a library by checking for brickModule in package.json
      const packageJsonPath = path.join(
        this.config.projectRoot,
        "package.json"
      );
      let isLibrary = false;

      if (await fs.pathExists(packageJsonPath)) {
        const packageJson = await fs.readJson(packageJsonPath);
        isLibrary = !!packageJson.brickModule;
      }

      this.config.libraryMode = isLibrary;

      // Clean output directories if requested (after detecting library mode)
      if (this.config.clean) {
        this.log("🧹 Cleaning output directories...");
        await this.cleanOutputDirectories(isLibrary);
        this.log("✅ Output directories cleaned");
      }

      // Log platform selection
      if (this.config.platforms) {
        const selectedPlatforms = this.config.platforms.join(", ");
        this.log(
          `📦 ${
            isLibrary ? "Library" : "App"
          } mode - generating for selected platforms: ${selectedPlatforms}`
        );
      } else {
        this.log(
          `📦 ${
            isLibrary ? "Library" : "App"
          } mode - generating for all platforms`
        );
      }

      // 2. Initialize components with configuration
      const componentConfig = this.createComponentConfig();
      this.scanner = new Scanner(componentConfig);
      this.parser = new Parser(componentConfig);
      this.platformRegistry = new PlatformRegistry(componentConfig);
      this.generator = new Generator(componentConfig, this.platformRegistry);
      this.libraryGenerator = new LibraryGenerator(
        componentConfig,
        this.platformRegistry
      );

      this.log("🔍 Scanning for Brick modules...");

      // 1. Scan for brick modules
      const scannedModules = await this.scanner.scanBrickModules();

      if (this.config.debug) {
        console.log(
          `[scanner] Found ${scannedModules.length} modules:`,
          scannedModules.map((m) => m.name)
        );
      }

      if (scannedModules.length === 0) {
        result.warnings.push("No Brick modules found in dependencies");
        result.success = true;
        result.duration = Date.now() - startTime;
        return result;
      }

      this.log(`📦 Found ${scannedModules.length} Brick modules`);

      // 2. Parse module specifications
      this.log("📝 Parsing module specifications...");
      const parsedModules = [];
      const parsingErrors: BrickCodegenError[] = [];

      // Collect all parsing errors without stopping
      for (const module of scannedModules) {
        try {
          const parsed = await this.parser.parseModuleSpec(module);
          parsedModules.push(...parsed);

          // Register source mappings for events
          for (const parsedModule of parsed) {
            if (parsedModule.events) {
              for (const event of parsedModule.events) {
                const generatedName = `${parsedModule.moduleName}_on${event.name
                  .charAt(0)
                  .toUpperCase()}${event.name.slice(1)}`;
                this.sourceMap.addMapping({
                  generatedName,
                  sourceFile: event.sourceFile || parsedModule.specPath,
                  sourceName: event.name,
                  sourceLine: event.sourceLine,
                  sourceColumn: event.sourceColumn,
                  moduleName: parsedModule.moduleName,
                  type: "event",
                });
              }
            }
          }
        } catch (error) {
          // Wrap or preserve BrickCodegenError
          const brickError =
            error instanceof BrickCodegenError
              ? error
              : wrapError(error, {
                  filePath: module.specPath,
                  moduleName: module.name,
                });

          parsingErrors.push(brickError);
          result.errors.push(brickError.message);
        }
      }

      // Store detailed errors for reporting
      if (parsingErrors.length > 0) {
        result.detailedErrors = parsingErrors;
        result.success = false;
        result.duration = Date.now() - startTime;
        return result;
      }

      if (parsedModules.length === 0) {
        result.errors.push("No valid module specifications found");
        result.duration = Date.now() - startTime;
        return result;
      }

      result.moduleCount = parsedModules.length;

      // 3. Pass scanned modules to platform registry for dependency detection
      this.platformRegistry.setScannedModules(scannedModules);

      // 4. Generate files based on project type
      if (isLibrary) {
        // Library mode: Generate only types/protocols to typegen/
        this.log("🏗️  Library mode: Generating types to typegen/...");
        const libraryFiles = await this.libraryGenerator.generateLibraryCode(
          parsedModules
        );
        result.generatedFiles.push(...libraryFiles);

        // Skip React Native codegen and other user project specific steps
        result.success = true;
        this.log(
          "✅ Library type generation to typegen/ completed successfully!"
        );
      } else {
        // User project mode: Generate bridge code to .brick/
        this.log("⚡ User project mode: Generating bridge code to .brick/...");

        // Generate bridge code for all modules (library + local)
        const generatedFiles = await this.generator.generateAll(parsedModules);
        result.generatedFiles.push(...generatedFiles);

        // 5. Run React Native codegen
        this.log("🚀 Running React Native codegen...");
        try {
          await this.runCodegen();
          await this.moveCodegenResults();
          this.log("✅ React Native codegen completed successfully!");

          // 6. Post-process generated header files
          this.log("🔧 Post-processing generated header files...");
          await this.postProcessHeaderFiles();
          this.log("✅ Header file post-processing completed!");
        } catch (error) {
          // Parse and enhance React Native Codegen errors
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          const specFilePath = path.join(
            this.config.projectRoot,
            this.config.outputTypescript,
            "src/spec/NativeBrickModule.ts"
          );

          const codegenError = createCodegenError(
            errorMessage,
            specFilePath,
            this.sourceMap
          );

          // Store as detailed error
          if (!result.detailedErrors) {
            result.detailedErrors = [];
          }
          result.detailedErrors.push(codegenError);
          result.errors.push(codegenError.message);

          this.log("⏩ React Native codegen failed - see errors above");
          throw codegenError;
        }

        result.success = true;
        this.log("✅ Code generation completed successfully!");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      result.errors.push(`Code generation failed: ${errorMessage}`);
      this.log("❌ Code generation failed:", error);
    }

    result.duration = Date.now() - startTime;
    return result;
  }

  // Rest of the methods remain the same as the original implementation
  private async runCodegen(): Promise<void> {
    const brickDir = path.join(
      this.config.projectRoot,
      this.config.outputTypescript
    );
    const selectedPlatforms = this.config.platforms;

    try {
      // Resolve the codegen script path using require.resolve
      const codegenScriptPath = require.resolve(
        "react-native/scripts/generate-codegen-artifacts.js",
        {
          paths: [this.config.projectRoot],
        }
      );

      // Run iOS codegen if selected (or if no platforms specified)
      if (!selectedPlatforms || selectedPlatforms.includes("ios")) {
        this.log("📱 Running React Native codegen for iOS...");

        const iosResult = await execa(
          "node",
          [codegenScriptPath, "-p", brickDir, "-t", "ios", "-o", "ios"],
          { cwd: this.config.projectRoot }
        );

        if (iosResult.stdout) {
          this.log("iOS codegen output:", iosResult.stdout);
        }
      }

      // Run Android codegen if selected (or if no platforms specified)
      if (!selectedPlatforms || selectedPlatforms.includes("android")) {
        this.log("🤖 Running React Native codegen for Android...");

        const androidResult = await execa(
          "node",
          [codegenScriptPath, "-p", brickDir, "-t", "android", "-o", "android"],
          { cwd: this.config.projectRoot }
        );

        if (androidResult.stdout) {
          this.log("Android codegen output:", androidResult.stdout);
        }
      }
    } catch (error) {
      throw new Error(
        `React Native codegen execution failed: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  private async moveCodegenResults(): Promise<void> {
    const selectedPlatforms = this.config.platforms;

    // Move iOS codegen results if iOS was generated
    if (!selectedPlatforms || selectedPlatforms.includes("ios")) {
      await this.moveIOSCodegenResults();
    }

    // Move Android codegen results if Android was generated
    if (!selectedPlatforms || selectedPlatforms.includes("android")) {
      await this.moveAndroidCodegenResults();
    }
  }

  private async moveIOSCodegenResults(): Promise<void> {
    // Use configured output path instead of hardcoded ios folder
    const iosBrickDir = path.join(
      this.config.projectRoot,
      this.config.outputIos
    );
    // Build artifacts are created relative to project root
    const buildDir = path.join(this.config.projectRoot, "ios", "build");
    const generatedDir = path.join(buildDir, "generated", "ios");

    try {
      await fs.ensureDir(iosBrickDir);

      if (await fs.pathExists(generatedDir)) {
        const generatedFiles = await fs.readdir(generatedDir);

        for (const file of generatedFiles) {
          const sourcePath = path.join(generatedDir, file);
          const targetPath = path.join(iosBrickDir, file);

          if (await fs.pathExists(targetPath)) {
            await fs.remove(targetPath);
          }

          await fs.move(sourcePath, targetPath);
          const absolutePath = path.resolve(targetPath);
          console.log(
            `${pc.bold(
              pc.green("[Brick]")
            )} Generated artifact: ${absolutePath}`
          );
        }

        await this.filterBrickModuleFiles(iosBrickDir);

        if (await fs.pathExists(buildDir)) {
          await fs.remove(buildDir);
          this.log("🗑️ Cleaned up build directory");
        }
      } else {
        this.log("⚠️ No iOS codegen results found to move");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.log("⚠️ Failed to move iOS codegen results:", errorMessage);
    }
  }

  private async moveAndroidCodegenResults(): Promise<void> {
    // Build artifacts are created in standard locations during codegen
    // React Native always generates to android/android/app/build
    const androidBuildDir = path.join(
      this.config.projectRoot,
      "android",
      "android",
      "app",
      "build"
    );
    const generatedDir = path.join(
      androidBuildDir,
      "generated",
      "source",
      "codegen"
    );

    // Target directories for different file types - now in configured directory
    const brickDir = path.join(
      this.config.projectRoot,
      this.config.outputAndroid
    );
    const javaTargetDir = path.join(brickDir, "src", "main", "java");

    try {
      if (await fs.pathExists(generatedDir)) {
        this.log("📁 Processing Android codegen results...");

        // Copy JNI files (C++ headers and implementations) for New Architecture
        const jniSourceDir = path.join(generatedDir, "jni");
        this.log(`🔍 Checking JNI source directory: ${jniSourceDir}`);

        if (await fs.pathExists(jniSourceDir)) {
          const jniTargetDir = path.join(
            this.config.projectRoot,
            this.config.outputAndroidAppPath,
            "build/generated/autolinking/src/main/jni"
          );
          this.log(`📂 JNI target directory: ${jniTargetDir}`);

          await fs.ensureDir(jniTargetDir);
          await fs.copy(jniSourceDir, jniTargetDir, { overwrite: true });
          this.log(
            "📋 Copied React Native Codegen JNI files to autolinking directory"
          );

          // Log generated JNI files
          const jniFiles = await glob("**/*.{cpp,h}", {
            cwd: jniTargetDir,
          });
          for (const file of jniFiles) {
            const absolutePath = path.resolve(jniTargetDir, file);
            console.log(
              `${pc.bold(
                pc.green("[Brick]")
              )} Generated artifact: ${absolutePath}`
            );
          }
        } else {
          this.log(`⚠️ JNI source directory not found: ${jniSourceDir}`);
        }

        // Copy Java files (facebook specs)
        const javaSourceDir = path.join(generatedDir, "java");
        if (await fs.pathExists(javaSourceDir)) {
          await fs.ensureDir(javaTargetDir);

          // Copy com/facebook directory structure
          const facebookDir = path.join(javaSourceDir, "com", "facebook");
          if (await fs.pathExists(facebookDir)) {
            const targetFacebookDir = path.join(
              javaTargetDir,
              "com",
              "facebook"
            );
            await fs.ensureDir(targetFacebookDir);
            await fs.copy(facebookDir, targetFacebookDir, { overwrite: true });
            this.log("📋 Copied Facebook spec files to app directory");

            // Log generated Java files
            const specFiles = await glob("**/*.java", {
              cwd: targetFacebookDir,
            });
            for (const file of specFiles) {
              const absolutePath = path.resolve(targetFacebookDir, file);
              console.log(
                `${pc.bold(
                  pc.green("[Brick]")
                )} Generated artifact: ${absolutePath}`
              );
            }
          }
        }

        // Clean up build directory
        if (await fs.pathExists(androidBuildDir)) {
          await fs.remove(androidBuildDir);
          this.log("🗑️ Cleaned up Android build directory");
        }
      } else {
        this.log("⚠️ No Android codegen results found to move");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.log("⚠️ Failed to move Android codegen results:", errorMessage);
    }
  }

  private async filterBrickModuleFiles(iosBrickDir: string): Promise<void> {
    // Whitelist of patterns for files to keep
    const KEEP_PATTERNS = [
      /^BrickModule.*$/, // BrickModule.mm, BrickModuleImpl.swift, etc.
      /^BrickCodegen.*$/, // BrickCodegen.podspec, etc.
      /^BrickModuleProvider.*$/, // BrickModuleProvider files
      /^BrickModuleSpec.*$/, // BrickModuleSpec directory and files
      /^Types$/, // Types directory for local modules
    ];

    // System files that should never be removed
    const SYSTEM_FILES_TO_REMOVE = [
      "RCTAppDependencyProvider.h",
      "RCTAppDependencyProvider.mm",
      "RCTModulesConformingToProtocolsProvider.h",
      "RCTModulesConformingToProtocolsProvider.mm",
      "RCTModuleProviders.h",
      "RCTModuleProviders.mm",
      "RCTThirdPartyComponentsProvider.h",
      "RCTThirdPartyComponentsProvider.mm",
      "RCTUnstableModulesRequiringMainQueueSetupProvider.h",
      "RCTUnstableModulesRequiringMainQueueSetupProvider.mm",
      "ReactAppDependencyProvider.podspec",
      "ReactCodegen.podspec",
    ];

    try {
      // First, remove system files that are never needed
      for (const fileName of SYSTEM_FILES_TO_REMOVE) {
        const filePath = path.join(iosBrickDir, fileName);
        if (await fs.pathExists(filePath)) {
          await fs.remove(filePath);
        }
      }

      // Get all files in the directory
      const allFiles = await fs.readdir(iosBrickDir);

      // Check each file against whitelist patterns
      for (const file of allFiles) {
        const filePath = path.join(iosBrickDir, file);
        const stat = await fs.stat(filePath);

        // Skip directories that match BrickModuleSpec pattern
        if (stat.isDirectory() && file.match(/^BrickModuleSpec$/)) {
          continue;
        }

        // Check if file should be kept
        const shouldKeep = KEEP_PATTERNS.some((pattern) => pattern.test(file));

        if (!shouldKeep) {
          await fs.remove(filePath);
          this.log(`🗑️ Removed non-brick file: ${file}`);
        }
      }

      this.log("✅ Filtered to keep only Brick module files");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.log("⚠️ Failed to filter files:", errorMessage);
    }
  }

  private async postProcessHeaderFiles(): Promise<void> {
    const iosBrickDir = path.join(
      this.config.projectRoot,
      this.config.outputIos
    );

    try {
      const headerFiles = await glob("**/*.h", {
        cwd: iosBrickDir,
        absolute: true,
      });

      for (const headerFile of headerFiles) {
        try {
          const content = await fs.readFile(headerFile, "utf8");
          const processedContent = removePrivateAccessModifiers(content);

          if (processedContent !== content) {
            await fs.writeFile(headerFile, processedContent, "utf8");
            this.log(
              `🔧 Processed header file: ${path.relative(
                iosBrickDir,
                headerFile
              )}`
            );
          }
        } catch {
          // Silently handle individual file errors
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.log("⚠️ Failed to post-process header files:", errorMessage);
    }
  }

  private log(...args: any[]): void {
    console.log(pc.gray("[brick-codegen]"), ...args);
  }
}

export { Generator } from "./generator";
export { Parser } from "./parser";
// Export for CLI usage
export { Scanner } from "./scanner";
